import { useEffect, useRef } from 'react';
import { apiFetch } from './api';
import { useAuthStore } from '../store/auth.store';

/**
 * Heartbeat de inatividade baseado em interação humana (REQ-003 RN-02 / AB#85).
 *
 * Problema corrigido: até o AB#85, `ultima_atividade` da `Sessao` era
 * renovada pelo `POST /auth/refresh` — disparado automaticamente pelo
 * `apiFetch` em todo 401 e por timers de polling (TrialBanner,
 * SinoNotificacoes, páginas com refetch). Resultado: o tenant "ficava
 * ativo" para o backend mesmo sem ninguém na frente da tela.
 *
 * Solução: `useActivityHeartbeat` escuta interação real do usuário no
 * `document` (mousemove, keydown, scroll, touchstart) e a cada
 * `INTERVALO_MS` faz `POST /auth/heartbeat` SE — e somente se — houve
 * pelo menos um evento desde o último envio. O backend renova
 * `ultima_atividade` na sessão e o refresh deixa de tocar nesse campo.
 *
 * Por que não enviar em cada evento? Mil mousemove/seg ⇒ tempestade
 * de POSTs. O janelamento de {@link INTERVALO_MS} (30s) é o "debounce"
 * pedido no requisito — mesmo padrão de polling do `useDebouncedFilters`.
 *
 * Aba escondida (background): mousemove/keydown não disparam, então o
 * heartbeat naturalmente pausa. Não força nada nesse caso — a janela
 * voltando ao foco já gera um mousemove na primeira movimentação.
 */

const INTERVALO_MS = 30 * 1000;
const EVENTOS = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const;

export function useActivityHeartbeat(): void {
  const autenticado = useAuthStore((s) => s.autenticado);
  // Refs em vez de state pra não re-renderizar o componente que monta
  // o hook em cada movimento de mouse — listener tem que ser hot path.
  const interagiuRef = useRef<boolean>(false);
  const enviandoRef = useRef<boolean>(false);

  useEffect(() => {
    if (!autenticado) return undefined;

    const marcar = (): void => {
      interagiuRef.current = true;
    };
    // `passive: true` em todos os eventos — não precisamos de
    // preventDefault e queremos o caminho rápido do browser pra scroll.
    EVENTOS.forEach((tipo) => {
      document.addEventListener(tipo, marcar, { passive: true });
    });

    const tick = async (): Promise<void> => {
      if (!interagiuRef.current || enviandoRef.current) return;
      interagiuRef.current = false; // reset antes do fetch — se o usuário
      // continuar interagindo durante o request, a próxima janela já pega.
      enviandoRef.current = true;
      try {
        await apiFetch('/auth/heartbeat', { method: 'POST' });
      } catch {
        // Falha de rede / 401 já é tratada em apiFetch (refresh automático).
        // Aqui é só não estourar; a próxima janela tenta de novo.
      } finally {
        enviandoRef.current = false;
      }
    };

    const id = window.setInterval(() => {
      void tick();
    }, INTERVALO_MS);

    return () => {
      window.clearInterval(id);
      EVENTOS.forEach((tipo) => document.removeEventListener(tipo, marcar));
      interagiuRef.current = false;
    };
  }, [autenticado]);
}
